import { useEffect, useState } from "react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function UsersPage() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [u, a] = await Promise.all([api.get("/users"), api.get("/apps")]);
            setUsers(u.data);
            setApps(a.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        refresh();
    }, []);

    const openCreate = () => {
        setEditing({
            id: null,
            username: "",
            password: "",
            role: "user",
            allowed_apps: [],
        });
        setOpen(true);
    };
    const openEdit = (u) => {
        setEditing({
            id: u.id,
            username: u.username,
            password: "",
            role: u.role,
            allowed_apps: u.allowed_apps || [],
        });
        setOpen(true);
    };

    const save = async () => {
        try {
            if (editing.id) {
                const body = {
                    role: editing.role,
                    allowed_apps: editing.allowed_apps,
                };
                if (editing.password) body.password = editing.password;
                await api.put(`/users/${editing.id}`, body);
                toast.success("User updated");
            } else {
                await api.post("/users", {
                    username: editing.username,
                    password: editing.password,
                    role: editing.role,
                    allowed_apps: editing.allowed_apps,
                });
                toast.success("User created");
            }
            setOpen(false);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/users/${pendingDelete.id}`);
            toast.success("User removed");
            setPendingDelete(null);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const toggleAppAccess = (appId) => {
        const set = new Set(editing.allowed_apps);
        if (set.has(appId)) set.delete(appId);
        else set.add(appId);
        setEditing({ ...editing, allowed_apps: Array.from(set) });
    };

    return (
        <>
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 sm:px-10 py-10 sm:py-12" data-testid="users-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-3">
                            User Management
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold">Team & access</h1>
                        <p className="text-muted-foreground mt-2">
                            Create users and assign granular access to specific apps.
                        </p>
                    </div>
                    <Button onClick={openCreate} className="rounded-full h-11" data-testid="add-user-button">
                        <Plus className="h-4 w-4 mr-1.5" /> Add user
                    </Button>
                </div>

                <div className="glass-tile rounded-2xl divide-y divide-border overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-muted-foreground">Loading...</div>
                    ) : (
                        users.map((u) => (
                            <div
                                key={u.id}
                                className="flex items-center justify-between gap-4 px-6 py-4"
                                data-testid={`user-row-${u.username}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center font-semibold">
                                        {u.username[0]?.toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-semibold flex items-center gap-2">
                                            {u.username}
                                            {u.role === "admin" ? (
                                                <span className="text-xs px-2 py-0.5 bg-foreground text-background rounded-full font-medium flex items-center gap-1">
                                                    <ShieldCheck className="h-3 w-3" /> Admin
                                                </span>
                                            ) : (
                                                <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full font-medium flex items-center gap-1">
                                                    <UserIcon className="h-3 w-3" /> User
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            {u.role === "admin"
                                                ? "Access to all apps"
                                                : `${(u.allowed_apps || []).length} app(s) granted`}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="rounded-full"
                                        onClick={() => openEdit(u)}
                                        data-testid={`edit-user-${u.username}`}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    {u.id !== me?.id && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-full text-red-500 hover:text-red-500"
                                            onClick={() => setPendingDelete(u)}
                                            data-testid={`delete-user-${u.username}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </main>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-[520px]" data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {editing?.id ? "Edit user" : "Create user"}
                        </DialogTitle>
                        <DialogDescription>
                            {editing?.id
                                ? "Update role, password or app access."
                                : "Add a new user and assign app access."}
                        </DialogDescription>
                    </DialogHeader>
                    {editing && (
                        <div className="space-y-4 py-2">
                            {!editing.id && (
                                <div className="grid gap-1.5">
                                    <Label htmlFor="u-name">Username</Label>
                                    <Input
                                        id="u-name"
                                        value={editing.username}
                                        onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                                        data-testid="input-new-username"
                                    />
                                </div>
                            )}
                            <div className="grid gap-1.5">
                                <Label htmlFor="u-pwd">{editing.id ? "New password (optional)" : "Password"}</Label>
                                <Input
                                    id="u-pwd"
                                    type="password"
                                    value={editing.password}
                                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                                    placeholder="At least 6 characters"
                                    data-testid="input-user-password"
                                />
                            </div>
                            <div className="grid gap-1.5">
                                <Label>Role</Label>
                                <Select
                                    value={editing.role}
                                    onValueChange={(v) => setEditing({ ...editing, role: v })}
                                >
                                    <SelectTrigger data-testid="select-user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">User</SelectItem>
                                        <SelectItem value="admin">Admin</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <div className="flex items-center justify-between">
                                    <Label>App access</Label>
                                    <span className="text-xs text-muted-foreground">
                                        {editing.role === "admin" ? "Admins see everything" : `${editing.allowed_apps.length} selected`}
                                    </span>
                                </div>
                                {editing.role !== "admin" && (
                                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-border p-2">
                                        {apps.length === 0 && (
                                            <p className="text-sm text-muted-foreground p-2">No apps yet.</p>
                                        )}
                                        {apps.map((a) => {
                                            const checked = editing.allowed_apps.includes(a.id);
                                            return (
                                                <button
                                                    key={a.id}
                                                    type="button"
                                                    className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                                        checked ? "bg-foreground/10" : "hover:bg-foreground/5"
                                                    }`}
                                                    onClick={() => toggleAppAccess(a.id)}
                                                    data-testid={`access-toggle-${a.id}`}
                                                >
                                                    <span className="truncate font-medium">{a.name}</span>
                                                    <Switch checked={checked} onCheckedChange={() => toggleAppAccess(a.id)} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={save} data-testid="save-user">
                            {editing?.id ? "Save changes" : "Create user"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            "{pendingDelete?.username}" will lose access immediately.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
